import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { MARK_CUSTOMER_PICKUP_STOPS_COMPLETE } from 'src/graphql/requests/markCustomerPickupStopsComplete';
import { GMarkCustomerPickupStopsCompleteMutation } from 'src/graphql/types/generated';
import { logWarning } from 'src/services/datadog';
import {
  isSelectedNightEndBeforeWarehouseNightEnd,
  selectedWarehouseSelector,
} from 'src/state/selectors/delivery';
import type { RootState } from 'src/state/store';
import { CANNOT_CHANGE_DATA, firstUserErrorMessage } from 'src/utils';
import { NotificationType, showNotification } from '../notification';
import { getAllStopStatuses } from './getAllStopStatues';
import { getPrimaryStopStatuses } from './getPrimaryStopStatuses';
import { getRoutes } from './getRoutes';

type MarkCustomerPickupStopsCompleteArgs = {
  stopIds: string[];
  warehouseId: string;
  nightEnd: string;
};

export const markCustomerPickupStopsComplete = createAsyncThunk(
  'delivery/markCustomerPickupStopsComplete',
  async (
    { stopIds, warehouseId, nightEnd }: MarkCustomerPickupStopsCompleteArgs,
    thunkAPI,
  ): Promise<boolean> => {
    // An empty selection is rejected server-side as a top-level GraphQL error,
    // not a userErrors entry, so it would surface as a bare generic toast.
    // Callers guard too; this is the backstop.
    if (stopIds.length === 0) return false;

    // Defense in depth on an irreversible billing action. Completion delivers
    // orders at fulfilled quantity and the backend does not night-end-guard this
    // path, so the viewed-night check is the only gate. The UI already applies
    // it, but this thunk is the single chokepoint both the per-row and bulk
    // flows funnel through, so re-check here against a stale night or an
    // unloaded warehouse before anything is written.
    const state = thunkAPI.getState() as RootState;
    if (
      isSelectedNightEndBeforeWarehouseNightEnd(state) ||
      !selectedWarehouseSelector(state)
    ) {
      thunkAPI.dispatch(
        showNotification({
          type: NotificationType.Error,
          message: CANNOT_CHANGE_DATA,
        }),
      );
      return false;
    }

    const { data, error } = await client
      .mutation<GMarkCustomerPickupStopsCompleteMutation>(
        MARK_CUSTOMER_PICKUP_STOPS_COMPLETE,
        { input: { stopIds } },
      )
      .toPromise();

    const payload = data?.markCustomerPickupStopsComplete;
    const userErrors = payload?.userErrors ?? [];

    if (error || !payload || userErrors.length > 0) {
      // This action bills the customer and cannot be undone, so a systematic
      // failure must be visible in prod, not only in the operator's toast. A
      // GraphQL 200 carrying userErrors is not a failed request, so
      // forwardErrorsToLogs never sees it.
      logWarning('customer_pickup.mark_complete_failed', {
        stopCount: stopIds.length,
        userError: userErrors[0]?.__typename,
        transportError: Boolean(error),
      });
      thunkAPI.dispatch(
        showNotification({
          type: NotificationType.Error,
          message: userErrors.length
            ? firstUserErrorMessage(userErrors)
            : undefined,
        }),
      );

      // A typed userError means the server disagreed with what this client
      // thought was completable, so the local routes are stale: the whole call
      // is rejected before anything is written, and a selection built on the
      // stale view would keep failing. Re-syncing lets the tab drop the ids
      // that are no longer eligible. A transport error teaches us nothing about
      // server state, so it gets no refetch.
      if (userErrors.length > 0) {
        thunkAPI.dispatch(getRoutes({ warehouseId, nightEnd }));
      }
      return false;
    }

    thunkAPI.dispatch(
      showNotification({
        type: NotificationType.Success,
        message:
          stopIds.length === 1
            ? 'Order marked as picked up.'
            : `${stopIds.length} orders marked as picked up.`,
      }),
    );

    // Three separate projections: state.routes carries completedAt (the Picked
    // Up pill), and the row status icons read whichever of the all/primary
    // stop-status caches the flag selects. refreshRouteData refetches all
    // three. The payload's `routes` is nullable, so refetching rather than
    // merging also covers a successful call that returns no routes.
    thunkAPI.dispatch(getRoutes({ warehouseId, nightEnd }));
    thunkAPI.dispatch(getAllStopStatuses({ warehouseId, nightEnd }));
    thunkAPI.dispatch(getPrimaryStopStatuses({ warehouseId, nightEnd }));

    return true;
  },
);
